import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const VideoLibrary = () => {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const res = await fetch(`${API_BASE}/video/library`);
      const data = await res.json();
      setVideos(data.videos || []);
      setCategories(data.categories || []);
      setByCategory(data.byCategory || []);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredVideos = activeCategory === 'all' 
    ? videos 
    : videos.filter(v => v.category === activeCategory);

  if (loading) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        color: '#94a3b8' 
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎬</div>
        Loading videos...
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        color: '#64748b',
        background: 'rgba(15, 23, 42, 0.5)',
        borderRadius: '0.75rem'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎬</div>
        <p style={{ margin: 0 }}>No videos published yet. Check back soon!</p>
      </div>
    );
  }

  return (
    <div>
      {/* Video Player Modal */}
      {selectedVideo && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          onClick={() => setSelectedVideo(null)}
        >
          <div 
            style={{
              background: '#1e293b',
              borderRadius: '1rem',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ position: 'relative' }}>
              <video 
                controls 
                autoPlay
                src={selectedVideo.video_url}
                style={{ 
                  width: '100%', 
                  borderRadius: '1rem 1rem 0 0',
                  maxHeight: '60vh',
                  background: '#000'
                }}
              />
              <button
                onClick={() => setSelectedVideo(null)}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'rgba(0, 0, 0, 0.7)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '1.2rem'
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <h3 style={{ margin: '0 0 0.5rem', color: 'white', fontSize: '1.25rem' }}>
                {selectedVideo.title}
              </h3>
              <p style={{ margin: '0 0 1rem', color: '#94a3b8', fontSize: '0.9rem' }}>
                {selectedVideo.description}
              </p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.8rem', color: '#64748b' }}>
                {selectedVideo.duration_seconds && (
                  <span>⏱️ {formatDuration(selectedVideo.duration_seconds)}</span>
                )}
                <span>👁️ {selectedVideo.view_count || 0} views</span>
                <span>📅 {formatDate(selectedVideo.published_at)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Filter - scrollable on mobile */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        flexWrap: 'nowrap',
        overflowX: 'auto',
        paddingBottom: '0.5rem',
        marginBottom: '1rem',
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        <button
          onClick={() => setActiveCategory('all')}
          style={{
            padding: '0.4rem 0.75rem',
            background: activeCategory === 'all' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(30, 41, 59, 0.8)',
            border: activeCategory === 'all' ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '2rem',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.8rem',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          All ({videos.length})
        </button>
        {byCategory.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            style={{
              padding: '0.4rem 0.75rem',
              background: activeCategory === cat.id ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(30, 41, 59, 0.8)',
              border: activeCategory === cat.id ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '2rem',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {cat.icon} {cat.name} ({cat.videos.length})
          </button>
        ))}
      </div>

      {/* Video Grid - responsive for mobile */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', 
        gap: '1rem' 
      }}>
        {filteredVideos.map(video => (
          <div
            key={video.video_id}
            onClick={() => setSelectedVideo(video)}
            style={{
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)',
              borderRadius: '0.75rem',
              overflow: 'hidden',
              cursor: 'pointer',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 10px 40px rgba(99, 102, 241, 0.2)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {/* Thumbnail */}
            <div style={{ 
              position: 'relative',
              paddingTop: '56.25%', // 16:9 aspect ratio
              background: '#0f172a'
            }}>
              {video.thumbnail_url ? (
                <img 
                  src={video.thumbnail_url} 
                  alt={video.title}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #1e293b, #0f172a)',
                  fontSize: '3rem'
                }}>
                  🎬
                </div>
              )}
              
              {/* Play button overlay */}
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.3)',
                opacity: 0,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = 1}
              onMouseLeave={e => e.currentTarget.style.opacity = 0}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(99, 102, 241, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}>
                  ▶️
                </div>
              </div>

              {/* Duration badge */}
              {video.duration_seconds && (
                <div style={{
                  position: 'absolute',
                  bottom: '0.5rem',
                  right: '0.5rem',
                  background: 'rgba(0, 0, 0, 0.8)',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.75rem',
                  color: 'white'
                }}>
                  {formatDuration(video.duration_seconds)}
                </div>
              )}
            </div>

            {/* Info */}
            <div style={{ padding: '1rem' }}>
              <h4 style={{ 
                margin: '0 0 0.5rem', 
                color: 'white', 
                fontSize: '0.95rem',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                {video.title}
              </h4>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.75rem', 
                color: '#64748b' 
              }}>
                <span>👁️ {video.view_count || 0} views</span>
                <span>{formatDate(video.published_at)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VideoLibrary;